import React from "reactn"
import { GlobalContext, SiteContext } from "./src/state/rootstate"
import { Amplify } from "aws-amplify";
import { Auth } from 'aws-amplify';
import { setUser } from './src/components/auth/auth'
import CommonState from "./src/state/commonState";
import * as Sentry from "@sentry/gatsby";

// Explicitly initialize Sentry if not already initialized
// The plugin should handle this, but we ensure it happens
if (typeof window !== 'undefined') {
  const activeEnv = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || 'production';

  // Check if Sentry is already initialized (plugin might have done it)
  if (!window.__SENTRY_INITIALIZED__) {
    console.log('[SENTRY] Initializing Sentry in gatsby-browser.js, environment:', activeEnv);

    // Enable debug for development and staging, not production
    const isDebug = activeEnv === 'development' || activeEnv === 'staging';

    Sentry.init({
      dsn: "https://0a2363fad1c287ccd19c4bc0dd352ac1@o4510460032319488.ingest.de.sentry.io/4510460039331920",
      environment: activeEnv,
      tracesSampleRate: 1.0,
      enabled: true,
      sendDefaultPii: true,
      debug: isDebug,
      // Get all default integrations (includes GlobalHandlers, BrowserTracing, etc.)
      integrations: Sentry.getDefaultIntegrations({}),
      // Capture unhandled errors and promise rejections automatically
      // GlobalHandlers integration is included by default
      beforeSend(event, hint) {
        console.log('[SENTRY] beforeSend called:', {
          eventId: event.event_id,
          message: event.message,
          exception: event.exception?.values?.[0]?.value,
          level: event.level,
          environment: event.environment,
          release: event.release,
          hasException: !!event.exception,
          hasMessage: !!event.message,
          tags: event.tags
        });

        // Don't send errors from browser extensions
        if (event.exception?.values?.[0]?.value?.includes('Extension')) {
          console.log('[SENTRY] Filtered out browser extension error');
          return null;
        }

        // Make sure we have something to send
        if (!event.exception && !event.message) {
          console.warn('[SENTRY] Event has no exception or message, skipping');
          return null;
        }

        // Add unique fingerprint for test events to prevent deduplication
        if (event.tags?.source === 'cart_test' || event.tags?.source === 'manual_test') {
          event.fingerprint = [`test-${Date.now()}`];
          console.log('[SENTRY] Added unique fingerprint for test event');
        }

        console.log('[SENTRY] Sending event to Sentry - Event ID:', event.event_id);
        return event;
      },
    });

    window.__SENTRY_INITIALIZED__ = true;
    console.log('[SENTRY] Sentry initialized successfully');
  } else {
    console.log('[SENTRY] Sentry already initialized by plugin');
  }

  // Intercept all network requests to capture failures
  // This captures fetch API calls
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || 'unknown';

    try {
      const response = await originalFetch.apply(this, args);

      // Capture failed HTTP responses (4xx, 5xx)
      if (!response.ok && response.status >= 400) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.url = url;
        error.method = args[1]?.method || 'GET';

        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, {
            tags: {
              source: 'network_error',
              httpStatus: response.status,
              method: error.method
            },
            extra: {
              url: url,
              status: response.status,
              statusText: response.statusText,
              method: error.method
            },
            level: response.status >= 500 ? 'error' : 'warning'
          });
        }
      }

      return response;
    } catch (error) {
      // Network errors (CORS, connection refused, timeout, etc.)
      console.error('[NETWORK ERROR] Fetch failed:', url, error);

      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, {
          tags: {
            source: 'network_fetch_error',
            errorType: error.name || 'NetworkError'
          },
          extra: {
            url: url,
            method: args[1]?.method || 'GET',
            errorMessage: error.message,
            errorStack: error.stack
          },
          level: 'error'
        });
      }

      throw error;
    }
  };

  // Intercept XMLHttpRequest to capture failures
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._sentryMethod = method;
    this._sentryUrl = url;
    return originalXHROpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    this.addEventListener('error', function() {
      const error = new Error(`XHR Error: ${this._sentryMethod} ${this._sentryUrl}`);
      error.status = this.status;
      error.url = this._sentryUrl;
      error.method = this._sentryMethod;

      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error, {
          tags: {
            source: 'network_xhr_error',
            method: this._sentryMethod
          },
          extra: {
            url: this._sentryUrl,
            status: this.status,
            statusText: this.statusText,
            method: this._sentryMethod
          },
          level: 'error'
        });
      }
    });

    this.addEventListener('load', function() {
      // Capture failed HTTP responses (4xx, 5xx)
      if (this.status >= 400) {
        const error = new Error(`HTTP ${this.status}: ${this.statusText}`);
        error.status = this.status;
        error.url = this._sentryUrl;
        error.method = this._sentryMethod;

        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, {
            tags: {
              source: 'network_xhr_error',
              httpStatus: this.status,
              method: this._sentryMethod
            },
            extra: {
              url: this._sentryUrl,
              status: this.status,
              statusText: this.statusText,
              method: this._sentryMethod
            },
            level: this.status >= 500 ? 'error' : 'warning'
          });
        }
      }
    });

    return originalXHRSend.apply(this, args);
  };

  console.log('[SENTRY] Network interceptors installed for fetch and XMLHttpRequest');
}

// Global error handlers to catch all unhandled errors
if (typeof window !== 'undefined') {
  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    console.log('[SENTRY] Unhandled error caught:', event);
    Sentry.captureException(event.error || new Error(event.message), {
      tags: { source: 'unhandled_error' },
      extra: { filename: event.filename, lineno: event.lineno, colno: event.colno }
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.log('[SENTRY] Unhandled rejection caught:', event);
    Sentry.captureException(event.reason || new Error('Unhandled promise rejection'), {
      tags: { source: 'unhandled_rejection' }
    });
  });

  // Test function - call window.testSentry() in console to test
  window.testSentry = () => {
    console.log('[SENTRY] Testing error capture...');
    const testError = new Error(`Sentry Test Error - ${new Date().toISOString()}`);
    Sentry.captureException(testError, {
      tags: {
        source: 'manual_test',
        test: 'true'
      },
      extra: {
        test: true,
        timestamp: Date.now(),
        random: Math.random()
      },
      level: 'error'
    });
    console.log('[SENTRY] Test error sent to Sentry - check network tab for event_id');
  };

  // Test with message instead of exception
  window.testSentryMessage = () => {
    console.log('[SENTRY] Testing message capture...');
    Sentry.captureMessage(`Sentry Test Message - ${new Date().toISOString()}`, {
      tags: {
        source: 'manual_test_message',
        test: 'true'
      },
      level: 'info'
    });
    console.log('[SENTRY] Test message sent to Sentry');
  };

  // Verify Sentry is working
  window.checkSentry = () => {
    const client = Sentry?.getClient?.();
    const integrations = client?.getIntegration?.('GlobalHandlers');

    console.log('[SENTRY CHECK] Sentry status:', {
      available: typeof Sentry !== 'undefined',
      initialized: window.__SENTRY_INITIALIZED__ || false,
      hasCaptureException: typeof Sentry?.captureException === 'function',
      hasCaptureMessage: typeof Sentry?.captureMessage === 'function',
      client: client ? 'initialized' : 'not initialized',
      hasGlobalHandlers: !!integrations,
      integrations: client?.getIntegrations?.() ? Object.keys(client.getIntegrations()) : 'unknown'
    });

    if (typeof Sentry === 'undefined') {
      console.error('[SENTRY CHECK] Sentry is not loaded!');
      return false;
    }

    if (!Sentry.captureException) {
      console.error('[SENTRY CHECK] Sentry.captureException is not available!');
      return false;
    }

    if (!integrations) {
      console.warn('[SENTRY CHECK] GlobalHandlers integration not found - unhandled errors may not be auto-captured!');
    }

    console.log('[SENTRY CHECK] Sentry appears to be working correctly');
    return true;
  };

  // Test unhandled error capture
  window.testUnhandledError = () => {
    console.log('[SENTRY TEST] Throwing unhandled error (should be auto-captured)...');
    // This should be automatically caught by Sentry's GlobalHandlers
    setTimeout(() => {
      throw new Error(`Unhandled Test Error - ${new Date().toISOString()}`);
    }, 100);
  };

  // Test unhandled promise rejection
  window.testUnhandledRejection = () => {
    console.log('[SENTRY TEST] Creating unhandled promise rejection (should be auto-captured)...');
    // This should be automatically caught by Sentry's GlobalHandlers
    Promise.reject(new Error(`Unhandled Promise Rejection - ${new Date().toISOString()}`));
  };
}

// Amplify.Logger.LOG_LEVEL = 'DEBUG';
Amplify.configure({
  Auth: {
    mandatorySignIn: process.env.GATSBY_MANDATORY_SIGNIN && process.env.GATSBY_MANDATORY_SIGNIN.toLocaleLowerCase() === 'true' ? true : false,
    region: 'eu-west-1',
    identityPoolRegion: 'eu-west-1',
    userPoolId: process.env.GATSBY_USER_POOL_ID,
    identityPoolId: process.env.GATSBY_IDENTITY_POOL_ID,
    userPoolWebClientId: process.env.GATSBY_WEB_CLIENT_ID,
  },
  API: {
    endpoints: [
      {
        name: "api",
        region: 'eu-west-1',
        endpoint: process.env.GATSBY_API_ENDPOINT
      }
    ]
  }
})

export const onRouteUpdate = (state, page, pages) => {
  Auth.currentAuthenticatedUser()
    .then(user => {
      const userInfo = {
        ...user.attributes,
        username: user.username
      }
      setUser(userInfo)
    })
    .catch(err => {
      window.localStorage.setItem('gau', null)
    })
}

export const wrapRootElement = ({ element }) => (
      <SiteContext>{element}</SiteContext>
)

export function wrapPageElement({ element, props }) {
  const { location } = props
  const Layout = element.type.Layout ?? React.Fragment
  return (
    <GlobalContext.Consumer >
        {globalState => (
            <CommonState pageContext={props.pageContext} location={location} globalState={globalState}>
              <Layout location={location} indexName={props.pageContext.index}  globalState={globalState} {...props}>{element}</Layout>
              </CommonState>
            )}
      </GlobalContext.Consumer>
  )
}


export const onClientEntry = () => {
  // Warm up the cart lambda when someone first visits the website
  // This ensures the lambda is ready for subsequent cart operations
  if (typeof window !== 'undefined') {
    // Use a small delay to ensure the app is fully loaded
    setTimeout(() => {
      try {
        // Import the cart warming function and call it
        import('./src/state/network').then(({ warmUpCartLambda }) => {
          warmUpCartLambda()
        })
      } catch (error) {
        // Silently handle any errors during cart warming
        console.log('Cart warming initialization completed')
      }
    }, 1000) // 1 second delay to ensure app is loaded
  }
}

//   const LogRocket = require('logrocket')
//   LogRocket.init('ingmarson/ingmarson-website', {
//     dom: {
//       textSanitizer: false,
//       inputSanitizer: true,
//     }
//   });
//   const setupLogRocketReact = require('logrocket-react')
//   setupLogRocketReact(LogRocket)

// }

// export function onServiceWorkerUpdateReady() { window.location.reload() };



